<template>
  <div class="w-screen h-screen bg-[#00000061] fixed z-50 top-0 left-0">
    <Dialog
      v-model:visible="showImportModal"
      :draggable="false"
      :breakpoints="{ '960px': '95vw', '640px': '95vw' }"
      :style="{ width: '40vw', minWidth: '360px' }"
      :modal="true"
      contentStyle=""
      class="dialog"
      @update:visible="onCloseImportModal"
    >
      <template #header>
        <section class="pb-0 w-full">
          <h4>
            {{
              $t("labels.generic", {
                pt: `Importar alunos`,
                en: `Import students`,
              })
            }}
          </h4>
        </section>
      </template>
      <section>
        <div class="w-full flex flex-col gap-4">
          <div class="flex items-center gap-8">
            <div v-for="(item, i) in radios" :key="i">
              <RadioButton
                :id="item.name"
                v-model="importType"
                :inputId="`id-${item.name}`"
                :value="item.value"
                name="radio-listed"
                class="custom-radio"
              />
              <label
                :for="`id-${item.name}`"
                :class="['font-bold ml-3 cursor-pointer']"
              >
                {{ item.name }}
              </label>
            </div>
          </div>
          <AutoComplete
            id="values-input"
            v-model="currentValue"
            :suggestions="items"
            :forceSelection="true"
            @complete="searchFor($event)"
            optionLabel="name"
            :placeholder="importType ? 'Escolha o curso' : 'Escolha a turma'"
            :class="`w-full`"
          />
        </div>
        <div class="mt-8 flex w-ful justify-center items-center">
          <ImportCSV
            id="import-user-btn"
            @onUpload="onUpload($event)"
            @onDownload="downloadCSV"
            :showErrors="true"
            :data="importData"
            :openModal="displayModal"
            btnStyle="btn-primary w-full"
            btnIcon="md-uploadfile-round"
            :btnDisabled="currentValue?.id ? false : true"
            :buttons="{
              first: $t('labels.generic', {
                pt: 'Selecionar arquivo',
                en: 'Select file',
              }),
              second: $t('labels.generic', {
                pt: 'Importar novo documento',
                en: 'Import new document',
              }),
            }"
          />
        </div>
      </section>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { Classroom } from "@/models/classrooms.model";
import { Course } from "@/models/courses.model";
import { QueryParams } from "@/models/query-params.model";
import { CsvFile } from "@/models/user.model";
import { ClassesRest } from "@/services/rest/classes.rest";
import { CoursesRest } from "@/services/rest/courses.rest";
import { UsersRest } from "@/services/rest/users.rest";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import saveAs from "file-saver";
import { ClassesService } from "@/views/Classes/Content/classes.service";
import { CourseService } from "@/views/Course/course.service";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import { UsersService } from "../users.service";

export default defineComponent({
  name: "importModal",
  emits: ["onCloseImportModal", "onUpdateTable"],
  props: {
    showModal: { type: Boolean, default: false },
  },
  computed: {
    service(): ClassesService {
      return new ClassesService();
    },
    serviceCourse(): CourseService {
      return new CourseService();
    },
    serviceUser(): UsersService {
      return new UsersService();
    },
    rest(): ClassesRest {
      return new ClassesRest();
    },
    restCourse(): CoursesRest {
      return new CoursesRest();
    },
    restUsers(): UsersRest {
      return new UsersRest();
    },
  },
  mounted() {
    this.showImportModal = this.showModal;
  },
  watch: {
    importType() {
      this.currentValue = null!;
    },
  },
  data() {
    return {
      items: [] as Classroom[] | Course[],
      currentValue: new Classroom() || new Course(),
      params: new QueryParams(),
      importData: new CsvFile(),
      displayModal: false as boolean,
      showImportModal: false as boolean,
      importType: true,
      radios: [
        {
          name: "Selecionar Curso",
          value: true,
        },
        {
          name: "Selecionar turma",
          value: false,
        },
      ] as { name: string; value: boolean }[],
    };
  },
  methods: {
    onCloseImportModal() {
      this.$emit("onCloseImportModal");
    },
    searchFor(event): void {
      const { query } = event;
      const params = { name: query.toLowerCase() };
      if (this.importType) this.getCourses(params);
      else this.getClasses(params);
    },
    getClasses(params): void {
      this.service.classes.subscribe({
        next: (response: any) => {
          this.items = response.content;
        },
      });
      this.service.getAllClasses({ page: 0, size: 30, name: params.name });
    },
    getCourses(params): void {
      this.serviceCourse.courses.subscribe({
        next: (response: any) => {
          this.items = response.content;
        },
      });
      this.serviceCourse.getCourses({ page: 0, size: 30, name: params.name });
    },
    onUpload(event): void {
      this.displayModal = true;
      this.importData.fileError = false;
      var { files } = event.target;
      if (files[0]?.type == "text/csv") {
        const formData = new FormData();
        formData.append("file", files[0]);
        this.importData = new CsvFile();
        this.importData.fileError = false;
        this.importData.file.name = files[0]?.name;
        if (this.importType) this.importByCourse(formData);
        else this.importByClassroom(formData);
      } else {
        this.importData = new CsvFile();
      }
    },
    downloadCSV(): void {
      const blob = new Blob([this.importData?.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      saveAs(url, `relatório_de _erros.csv`);
    },
    importResponse(response): void {
      if (response) {
        const csv: any = this.serviceUser.csvToArray(response);
        this.importData.csv = response;
        csv?.splice(1, 1);
        this.importData.errors = csv;
        this.$toast.add(ToastFactory.error(Messages.UPLOAD));
      } else {
        this.displayModal = false;
        this.$emit("onUpdateTable");
        this.onCloseImportModal();
        this.$toast.add(ToastFactory.success(Messages.USERS_ADDED));
      }
    },
    importByCourse(formData): void {
      this.restUsers
        .importUsersByCourse(this.currentValue?.id, formData)
        .pipe(delay(3000))
        .subscribe({
          next: (response) => this.importResponse(response),
          error: () => {
            this.importData = new CsvFile();
          },
        });
    },
    importByClassroom(formData): void {
      this.rest
        .importUsersByClassroom(this.currentValue?.id, formData)
        .pipe(delay(3000))
        .subscribe({
          next: (response) => this.importResponse(response),
          error: () => {
            this.importData = new CsvFile();
          },
        });
    },
  },
});
</script>
